/**
 * Logger centralizado para advup
 *
 * Este utilitário permite controlar logs de debug baseado no ambiente:
 * - Em desenvolvimento (DEV): todos os logs são exibidos
 * - Em produção (PROD): apenas warn e error são exibidos
 *
 * Uso:
 *   import logger from 'shared/helpers/logger';
 *   logger.log('mensagem normal');
 *   logger.debug('[DEBUG]', 'mensagem de debug');
 *   logger.warn('aviso importante');
 *   logger.error('erro crítico');
 */

const isProduction =
  typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env.PROD
    : process.env.NODE_ENV === 'production';

const isDevelopment =
  typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env.DEV
    : process.env.NODE_ENV !== 'production';

/* eslint-disable class-methods-use-this */
class Logger {
  /**
   * Log normal - suprimido em produção
   * @param {string} message - Mensagem principal
   * @param {...any} args - Argumentos adicionais
   */
  log(message, ...args) {
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.log(message, ...args);
    }
  }

  /**
   * Log de debug - apenas em desenvolvimento
   * @param {string} message - Mensagem principal
   * @param {...any} args - Argumentos adicionais
   */
  debug(message, ...args) {
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.debug(message, ...args);
    }
  }

  /**
   * Log de informação - suprimido em produção
   * @param {string} message - Mensagem principal
   * @param {...any} args - Argumentos adicionais
   */
  info(message, ...args) {
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.info(message, ...args);
    }
  }

  /**
   * Aviso - sempre exibido (importante para operações)
   * @param {string} message - Mensagem principal
   * @param {...any} args - Argumentos adicionais
   */
  warn(message, ...args) {
    // eslint-disable-next-line no-console
    console.warn(message, ...args);
  }

  /**
   * Erro - sempre exibido (crítico para debugging)
   * @param {string} message - Mensagem principal
   * @param {...any} args - Argumentos adicionais
   */
  error(message, ...args) {
    // eslint-disable-next-line no-console
    console.error(message, ...args);
  }

  /**
   * Log de grupo - suprimido em produção
   * Útil para agrupar logs relacionados
   * @param {string} label - Rótulo do grupo
   */
  group(label) {
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.group(label);
    }
  }

  /**
   * Grupo colapsado - suprimido em produção
   * @param {string} label - Rótulo do grupo
   */
  groupCollapsed(label) {
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.groupCollapsed(label);
    }
  }

  /**
   * Fim do grupo - suprimido em produção
   */
  groupEnd() {
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.groupEnd();
    }
  }

  /**
   * Tabela - suprimido em produção
   * @param {any} data - Dados para exibir em formato de tabela
   */
  table(data) {
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.table(data);
    }
  }

  /**
   * Timer - suprimido em produção
   * @param {string} label - Rótulo do timer
   */
  time(label) {
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.time(label);
    }
  }

  /**
   * Fim do timer - suprimido em produção
   * @param {string} label - Rótulo do timer
   */
  timeEnd(label) {
    if (!isProduction) {
      // eslint-disable-next-line no-console
      console.timeEnd(label);
    }
  }
}
/* eslint-enable class-methods-use-this */

const logger = new Logger();

export default logger;

// Exportações nomeadas para conveniência
export const { log, debug, info, warn, error } = logger;
